import { TFunction } from 'i18next';
import {
  faArrowsLeftRightToLine,
  faGlobe,
  faScaleUnbalanced,
  faGaugeHigh,
  faShip,
  faLocationDot,
  faEarthAmerica,
  faObjectGroup,
  faList,
  faShare,
  faNoteSticky,
} from '@fortawesome/free-solid-svg-icons';
import { ContainerDataType } from '@/types/containerData';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { formatNumber } from '@/helpers/numbers';

import styles from './ContainerTap.module.scss';
import { ArmadorIcon } from './ArmadorIcon';

const svgSizes = {
  width: '26px',
  height: '24px',
};
export interface CustomBoxPair {
  label: string;
  description: null | number | string;
}

export type CustomBoxProps = {
  pairs?: CustomBoxPair[];
  label: string;
  description: string | null | number;
  icon: React.ReactNode;
  iconColor: string;
  backgroundColor: string;
  iconBackgroundColor: string;
  svgStyle?: React.CSSProperties;
  className?: string;
};

/**
 * Retrieves translated data for the ContainerTap component.
 * @param {TFunction} t - The translation function.
 * @returns {Promise<CustomBoxProps[]>} - A promise that resolves to an array of CustomBoxProps.
 */

export const getTranslatedData = (
  t: TFunction,
  data: ContainerDataType
): Promise<CustomBoxProps[]> => {
  return new Promise((resolve) => {
    resolve([
      {
        label: t('shipowner'),
        description: data?.armador,
        icon: (
          <ArmadorIcon
            armador={data?.armador || ''}
            className={styles.iconMsc}
          />
        ),
        iconColor: 'black',
        backgroundColor: 'rgba(71, 128, 191, 0.1)',
        iconBackgroundColor: 'white',
        svgStyle: svgSizes,
        className: 'col md:col-3  ',
      },
      {
        label: t('type'),
        description:
          data?.tipo && data?.tamano && `${data?.tipo} ${data?.tamano}`,
        icon: <FontAwesomeIcon icon={faArrowsLeftRightToLine} />,
        iconColor: 'white',
        backgroundColor: 'rgba(71, 128, 191, 0.1)',
        iconBackgroundColor: '#4780bf',
        svgStyle: svgSizes,
        className: 'col md:col-3 ',
      },
      // {
      //   label: t('location'),
      //   description:
      //     data?.playa &&
      //     data?.estiba &&
      //     data?.calle &&
      //     data?.lateral &&
      //     data?.altura &&
      //     `
      //   ${data?.playa}:
      //   ${data?.estiba}:
      //   ${data?.calle}:
      //   ${data?.lateral}:
      //   ${data?.altura}:
      //   `,
      //   icon: <FontAwesomeIcon icon={faLocationDot} />,
      //   iconColor: 'white',
      //   backgroundColor: 'rgba(71, 128, 191, 0.1)',
      //   iconBackgroundColor: '#4780bf',
      //   svgStyle: svgSizes,
      //   className: 'col md:col-3 ',
      // },
      {
        label: t('state'),
        description: data?.estado,
        icon: <FontAwesomeIcon icon={faArrowsLeftRightToLine} />,
        iconColor: 'white',
        backgroundColor: 'rgba(250, 174, 48, 0.1)',
        iconBackgroundColor: '#faae30',
        svgStyle: svgSizes,
        className: 'col md:col-3 ',
      },
      {
        label: 'ISO',
        description: data?.iso,
        icon: <FontAwesomeIcon icon={faGlobe} />,
        iconColor: 'white',
        backgroundColor: 'rgba(71, 128, 191, 0.1)',
        iconBackgroundColor: '#4780bf',
        svgStyle: svgSizes,
        className: 'col md:col-3 ',
      },
      {
        label: 'Tara',
        description: data?.tara ? `${formatNumber(data?.tara || 0)} Kg` : null,
        icon: <FontAwesomeIcon icon={faScaleUnbalanced} />,
        iconColor: 'white',
        backgroundColor: 'rgba(71, 128, 191, 0.1)',
        iconBackgroundColor: '#4780bf',
        svgStyle: svgSizes,
        className: 'col md:col-3 ',
      },
      {
        label: 'MGW',
        description: data?.mgw ? `${formatNumber(data?.mgw || 0)} Kg` : null,
        icon: <FontAwesomeIcon icon={faGaugeHigh} />,
        iconColor: 'white',
        backgroundColor: 'rgba(71, 128, 191, 0.1)',
        iconBackgroundColor: '#4780bf',
        svgStyle: svgSizes,
        className: 'col md:col-3 ',
      },
    ]);
  });
};

export const getMockData = (
  t: TFunction,
  data: ContainerDataType
): Promise<CustomBoxProps[]> => {
  return new Promise((resolve) => {
    resolve([
      {
        label: 'Viaje',
        description: data?.viajeImport,
        icon: <FontAwesomeIcon icon={faShip} />,
        iconColor: 'white',
        backgroundColor: 'rgba(71, 128, 191, 0.1)',
        iconBackgroundColor: '#223466',
        className: 'col md:col-12',
      },
      {
        label: t('shipowner'),
        description: data?.armadorImport,
        icon: (
          <ArmadorIcon
            armador={data?.armador || ''}
            className={styles.iconMsc}
          />
        ),
        iconColor: 'black',
        backgroundColor: 'rgba(71, 128, 191, 0.1)',
        iconBackgroundColor: 'white',
        svgStyle: svgSizes,
        className: 'col md:col-6',
      },
      {
        label: 'Slot',
        description: data?.armadorSlotImport,
        icon: (
          <ArmadorIcon
            armador={data?.armador || ''}
            className={styles.iconMsc}
          />
        ),
        iconColor: 'black',
        backgroundColor: 'rgba(71, 128, 191, 0.1)',
        iconBackgroundColor: 'white',
        svgStyle: svgSizes,
        className: 'col md:col-6',
      },
      {
        label: 'Origen',
        description: data?.origenImport,
        icon: <FontAwesomeIcon icon={faEarthAmerica} />,
        iconColor: 'white',
        backgroundColor: 'rgba(71, 128, 191, 0.1)',
        iconBackgroundColor: '#223466',
        svgStyle: svgSizes,
        className: 'col md:col-6',
      },
      {
        label: 'Posición',
        description:
          data?.bayImport &&
          data?.lateralImport &&
          data?.alturaImport &&
          `
        ${data?.bayImport}:
        ${data?.lateralImport}:
        ${data?.alturaImport}
        `,
        icon: <FontAwesomeIcon icon={faLocationDot} />,
        iconColor: 'white',
        backgroundColor: 'rgba(71, 128, 191, 0.1)',
        iconBackgroundColor: '#223466',
        svgStyle: svgSizes,
        className: 'col md:col-6',
      },
      {
        label: 'Precintos',
        description: '',
        icon: <FontAwesomeIcon icon={faNoteSticky} />,
        iconColor: 'white',
        backgroundColor: 'rgba(71, 128, 191, 0.1)',
        iconBackgroundColor: '#223466',
        svgStyle: svgSizes,
        className: 'col md:col-12',
        pairs: [
          { label: 'Precinto 1', description: data?.precintoImport1 },
          {
            label: 'Precinto conv. 1',
            description: data?.precintoConvalidadoImport1,
          },
          { label: 'Precinto 2', description: data?.precintoImport2 },
          {
            label: 'Precinto conv. 2',
            description: data?.precintoConvalidadoImport2,
          },
          {
            label: 'Precinto 3',
            description: data?.precintoImport3,
          },
          {
            label: 'Precinto conv. 3',
            description: data?.precintoConvalidadoImport3,
          },
          {
            label: 'Precinto 4',
            description: data?.precintoImport4,
          },
          {
            label: 'Precinto conv. 4',
            description: data?.precintoConvalidadoImport4,
          },
        ],
      },
    ]);
  });
};
export const getMockDataArrivo = (
  t: TFunction,
  data: ContainerDataType
): Promise<CustomBoxProps[]> => {
  return new Promise((resolve) => {
    resolve([
      {
        label: 'Viaje',
        description: data?.viajeExport,
        icon: <FontAwesomeIcon icon={faShip} />,
        iconColor: 'white',
        backgroundColor: 'rgba(71, 128, 191, 0.1)',
        iconBackgroundColor: '#223466',
        className: 'col md:col-12',
      },
      {
        label: t('shipowner'),
        description: data?.armadorExport,
        icon: (
          <ArmadorIcon
            armador={data?.armador || ''}
            className={styles.iconMsc}
          />
        ),
        iconColor: 'black',
        backgroundColor: 'rgba(71, 128, 191, 0.1)',
        iconBackgroundColor: 'white',
        svgStyle: svgSizes,
        className: 'col md:col-6',
      },
      {
        label: 'Slot',
        description: data?.armadorSlotExport,
        icon: (
          <ArmadorIcon
            armador={data?.armador || ''}
            className={styles.iconMsc}
          />
        ),
        iconColor: 'black',
        backgroundColor: 'rgba(71, 128, 191, 0.1)',
        iconBackgroundColor: 'white',
        svgStyle: svgSizes,
        className: 'col md:col-6',
      },
      {
        label: 'Destino',
        description:
          data?.destino1 &&
          data?.destino2 &&
          `${data?.destino1} / ${data?.destino2}`,
        icon: <FontAwesomeIcon icon={faEarthAmerica} />,
        iconColor: 'white',
        backgroundColor: 'rgba(71, 128, 191, 0.1)',
        iconBackgroundColor: '#223466',
        svgStyle: svgSizes,
        className: 'col md:col-6',
      },
      {
        label: 'Posición',
        description:
          data?.bayExport &&
          data?.lateralExport &&
          data?.alturaExport &&
          `
        ${data?.bayExport}:
        ${data?.lateralExport}:
        ${data?.alturaExport}
        `,
        icon: <FontAwesomeIcon icon={faLocationDot} />,
        iconColor: 'white',
        backgroundColor: 'rgba(71, 128, 191, 0.1)',
        iconBackgroundColor: '#223466',
        svgStyle: svgSizes,
        className: 'col md:col-6',
      },
      {
        label: 'Booking',
        description: data?.booking,
        icon: <FontAwesomeIcon icon={faObjectGroup} />,
        iconColor: 'white',
        backgroundColor: 'rgba(71, 128, 191, 0.1)',
        iconBackgroundColor: '#223466',
        svgStyle: svgSizes,
        className: 'col md:col-12',
      },
      {
        label: 'Mercaderia',
        description: data?.descripcionMercaderia,
        icon: <FontAwesomeIcon icon={faList} />,
        iconColor: 'white',
        backgroundColor: 'rgba(71, 128, 191, 0.1)',
        iconBackgroundColor: '#223466',
        svgStyle: svgSizes,
        className: 'col md:col-6',
      },
      {
        label: 'Exportador',
        description: data?.exportador,
        icon: <FontAwesomeIcon icon={faShare} />,
        iconColor: 'white',
        backgroundColor: 'rgba(71, 128, 191, 0.1)',
        iconBackgroundColor: '#223466',
        svgStyle: svgSizes,
        className: 'col md:col-6',
      },
      {
        label: 'Precintos',
        description: '',
        icon: <FontAwesomeIcon icon={faNoteSticky} />,
        iconColor: 'white',
        backgroundColor: 'rgba(71, 128, 191, 0.1)',
        iconBackgroundColor: '#223466',
        svgStyle: svgSizes,
        className: 'col md:col-12',
        pairs: [
          { label: 'Precinto 1', description: data?.precintoExport1 },
          {
            label: 'Precinto conv. 1',
            description: data?.precintoConvalidadoExport1,
          },
          { label: 'Precinto 2', description: data?.precintoExport2 },
          {
            label: 'Precinto conv. 2',
            description: data?.precintoConvalidadoExport2,
          },
          {
            label: 'Precinto 3',
            description: data?.precintoExport3,
          },
          {
            label: 'Precinto conv. 3',
            description: data?.precintoConvalidadoExport3,
          },
          {
            label: 'Precinto 4',
            description: data?.precintoExport4,
          },
          {
            label: 'Precinto conv. 4',
            description: data?.precintoConvalidadoExport4,
          },
        ],
      },
    ]);
  });
};
